import * as React from 'react';

import { ModalModel, ModalContext } from '@notino/react-styleguide';
import {
  GetCatalogProductViewQuery,
  VtoBeautyType,
} from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { ModifaceModalRef } from '../ModiFaceModal';
import ModiFaceModal from '../ModiFaceModal/loadable';
import { trackModifaceOpen, trackModifaceClosed } from '../ModiFaceModal/utils';
import { hasVirtualTryOn } from '../utils';

import { useVirtualTryOnShades } from './vto';

interface UseModifaceFns {
  handleOpenModifaceModal: () => void;
  hasVariantModiface: (variant: { catalogId: string | number }) => boolean;
}

export const useModifaceFns = (
  product: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >,
  currentVariant: NonNullable<
    GetCatalogProductViewQuery['productDetailByCatalogMasterId']
  >['variants'][number]
): UseModifaceFns => {
  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const { getTimeFromInit } = useTrackingContext();
  const modifaceModalRef = React.useRef<ModifaceModalRef>(null);

  const variantsWithModiface = product.variants.filter(hasVirtualTryOn);

  const { data } = useVirtualTryOnShades({
    catalogIds: variantsWithModiface.map((variant) =>
      Number(variant.catalogId)
    ),
    disabled: variantsWithModiface.length === 0,
  });

  const vtoProducts = React.useMemo(
    () =>
      data?.VirtualTryOnShades?.products.filter(
        (p) => p.productType !== VtoBeautyType.None
      ) || [],
    [data]
  );

  const type = vtoProducts[0]?.productType;

  const hasVariantModiface: UseModifaceFns['hasVariantModiface'] =
    React.useCallback(
      (v) => vtoProducts.some((p) => p.productId === Number(v.catalogId)),
      [vtoProducts]
    );

  const handleOpenModifaceModal = React.useCallback(() => {
    if (!type) {
      return;
    }

    trackModifaceOpen({
      timing: getTimeFromInit(),
      product,
    });
    const timeOfOpen = new Date().getTime();

    toggleModal(
      <ErrorBoundary>
        <ModiFaceModal
          modifaceModalRef={modifaceModalRef}
          gtmProductId={currentVariant.productCode}
          onModalClose={hideModal}
          selectedVariant={currentVariant}
          variants={product.variants}
          type={type}
          modifaceVariants={vtoProducts.reduce(
            (acc, p) => ({
              ...acc,
              [p.productId]: p.shades,
            }),
            {}
          )}
          timeOfOpen={timeOfOpen}
        />
      </ErrorBoundary>,
      {
        noBorders: true,
        center: true,
        size: ModalModel.ModalSizes.s1200,
        fullscreenOnMobile: true,
        withFocusTrap: true,
        onClose: () => {
          hideModal();
          trackModifaceClosed({
            timing: getTimeFromInit(timeOfOpen),
            product,
            selectedVariant: currentVariant,
            viewedVariants: modifaceModalRef.current?.getViewedVariants() ?? 0,
          });
        },
      }
    );
  }, [
    getTimeFromInit,
    product,
    toggleModal,
    currentVariant,
    hideModal,
    type,
    vtoProducts,
  ]);

  return React.useMemo(
    () => ({
      handleOpenModifaceModal,
      hasVariantModiface,
    }),
    [handleOpenModifaceModal, hasVariantModiface]
  );
};
